import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Alert,
  Collapse,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  Euro as EuroIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

dayjs.locale('fr');

const daysOfWeek = [
  { key: 'monday', label: 'Lundi', short: 'Lun' },
  { key: 'tuesday', label: 'Mardi', short: 'Mar' },
  { key: 'wednesday', label: 'Mercredi', short: 'Mer' },
  { key: 'thursday', label: 'Jeudi', short: 'Jeu' },
  { key: 'friday', label: 'Vendredi', short: 'Ven' },
  { key: 'saturday', label: 'Samedi', short: 'Sam' },
  { key: 'sunday', label: 'Dimanche', short: 'Dim' }
];

const ScheduleTab = ({ userProfile, setSuccess, setError, centralServices, setCentralServices }) => {
  // Initialize schedule with default closed state to prevent red display issue
  const initializeSchedule = (profileSchedule) => {
    const defaultSchedule = {};
    daysOfWeek.forEach(({ key }) => {
      defaultSchedule[key] = {
        closed: true,
        open: '',
        close: ''
      };
    });
    
    if (profileSchedule && typeof profileSchedule === 'object') {
      return { ...defaultSchedule, ...profileSchedule };
    }
    
    return defaultSchedule;
  };

  const [schedule, setSchedule] = useState(() => initializeSchedule(userProfile?.schedule));
  const services = centralServices || [];
  const [loading, setLoading] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    duration: 30,
    price: ''
  });
  const [expandedSchedule, setExpandedSchedule] = useState(true);
  const [expandedServices, setExpandedServices] = useState(true);
  const [showScrollArrow, setShowScrollArrow] = useState(true);

  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle scroll arrow visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Hide arrow when near bottom of page
      if (scrollTop + windowHeight >= documentHeight - 100) {
        setShowScrollArrow(false);
      } else {
        setShowScrollArrow(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  // Update schedule when userProfile changes
  useEffect(() => {
    if (userProfile?.schedule) {
      setSchedule(initializeSchedule(userProfile.schedule));
    }
  }, [userProfile?.schedule]);

  // Services are now managed centrally, no need for local effect

  const handleScheduleChange = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleTimeChange = (day, field, time) => {
    const timeString = time ? time.format('HH:mm') : '';
    handleScheduleChange(day, field, timeString);
  };

  const saveSchedule = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        schedule: schedule,
        updatedAt: new Date()
      });

      setSuccess('Horaires mis à jour avec succès!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating schedule:', error);
      setError('Erreur lors de la mise à jour des horaires');
    } finally {
      setLoading(false);
    }
  };

  const openServiceDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        duration: service.duration,
        price: service.price
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        duration: 30,
        price: ''
      });
    }
    setServiceDialogOpen(true);
  };

  const saveService = async () => {
    if (!serviceForm.name || !serviceForm.price) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      let updatedServices;
      
      if (editingService) {
        updatedServices = services.map(service =>
          service.id === editingService.id
            ? { ...service, ...serviceForm }
            : service
        );
      } else {
        const newService = {
          id: Date.now().toString(),
          ...serviceForm,
          createdAt: new Date()
        };
        updatedServices = [...services, newService];
      }

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        services: updatedServices,
        updatedAt: new Date()
      });

      setCentralServices(updatedServices);
      setServiceDialogOpen(false);
      setSuccess(editingService ? 'Service mis à jour!' : 'Service ajouté!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving service:', error);
      setError('Erreur lors de la sauvegarde du service');
    }
  };

  const deleteService = async (serviceId) => {
    try {
      const updatedServices = services.filter(service => service.id !== serviceId);
      
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        services: updatedServices,
        updatedAt: new Date()
      });

      setCentralServices(updatedServices);
      setSuccess('Service supprimé!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Erreur lors de la suppression du service');
    }
  };

  const getOpenDaysCount = () => {
    return Object.values(schedule).filter(day => !day.closed).length;
  };

  const steps = [
    {
      label: 'Services et tarifs',
      description: 'Définissez vos services et leurs prix',
      icon: <BusinessIcon />,
      completed: services.length > 0
    },
    {
      label: 'Horaires d\'ouverture',
      description: 'Configurez vos heures de travail',
      icon: <ScheduleIcon />,
      completed: getOpenDaysCount() > 0
    }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, position: 'relative' }}>
        {/* Header with progress - Using theme colors */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            mb: 2, 
            background: 'linear-gradient(135deg, #1c1917 0%, #44403c 100%)',
            color: 'white',
            borderRadius: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={1.5}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                Configuration de votre établissement
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Définissez vos services et horaires pour permettre aux clients de réserver
              </Typography>
            </Box>
          </Box>

          {/* Progress indicator */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {steps.map((step, index) => (
              <Chip
                onClick={() => {}}
                key={index}
                icon={step.completed ? <CheckIcon /> : step.icon}
                label={step.label}
                variant={step.completed ? "filled" : "outlined"}
                size="small"
                sx={{
                  bgcolor: step.completed ? 'rgba(22, 163, 74, 0.2)' : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  border: step.completed ? 'none' : '1px solid rgba(255,255,255,0.3)',
                  '& .MuiChip-icon': { color: 'white' },
                  cursor: 'default',
                  pointerEvents: 'none'
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Services Section - Now first */}
        <Card sx={{ mb: 2, overflow: 'visible' }}>
          <CardContent sx={{ p: 0 }}>
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-between" 
              p={{ xs: 1, sm: 1.5, md: 2 }}
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpandedServices(!expandedServices)}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Services et tarifs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {services.length === 0 
                      ? "Ajoutez vos premiers services pour commencer" 
                      : `${services.length} service${services.length > 1 ? 's' : ''} configuré${services.length > 1 ? 's' : ''}`
                    }
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                {!isMobile && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openServiceDialog();
                    }}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    Ajouter
                  </Button>
                )}
                <IconButton size="small">
                  {expandedServices ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={expandedServices}>
              <Divider />
              <Box p={{ xs: 1, sm: 1.5, md: 2 }}>
                {isMobile && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openServiceDialog()}
                    sx={{ mb: 2 }}
                    size="small"
                  >
                    Ajouter un service
                  </Button>
                )}

                {services.length === 0 ? (
                  <Paper 
                    sx={{ 
                      p: 3, 
                      textAlign: 'center', 
                      bgcolor: 'grey.50',
                      border: '2px dashed',
                      borderColor: 'grey.300'
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1.5 }} />
                    <Typography variant="h6" gutterBottom>
                      Aucun service configuré
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Commencez par ajouter vos services (coupe, coloration, etc.) avec leurs durées et tarifs
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openServiceDialog()}
                    >
                      Ajouter votre premier service
                    </Button>
                  </Paper>
                ) : (
                  <Grid container spacing={1.5}>
                    {services.map((service) => (
                      <Grid item xs={12} sm={6} md={4} key={service.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 1,
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                {service.name}
                              </Typography>
                              <Box display="flex" gap={0.5}>
                                <Tooltip title="Modifier">
                                  <IconButton
                                    size="small"
                                    onClick={() => openServiceDialog(service)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => deleteService(service.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Chip
                                icon={<TimeIcon />}
                                label={`${service.duration} min`}
                                onClick={() => {}}
                                size="small"
                                variant="outlined"
                                sx={{ bgcolor: 'grey.100', cursor: 'default', pointerEvents: 'none' }}
                              />
                              <Chip
                                icon={<EuroIcon />}
                                label={`${service.price}€`}
                                onClick={() => {}}
                                size="small"
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  cursor: 'default',
                                  pointerEvents: 'none'
                                }}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Schedule Section - Now second */}
        <Card sx={{ mb: 2, overflow: 'visible' }}>
          <CardContent sx={{ p: 0 }}>
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-between" 
              p={{ xs: 1, sm: 1.5, md: 2 }}
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpandedSchedule(!expandedSchedule)}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Horaires d'ouverture
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getOpenDaysCount() === 0 
                      ? "Définissez vos heures d'ouverture" 
                      : `Ouvert ${getOpenDaysCount()} jour${getOpenDaysCount() > 1 ? 's' : ''} par semaine`
                    }
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small">
                {expandedSchedule ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={expandedSchedule}>
              <Divider />
              <Box p={{ xs: 1, sm: 1.5, md: 2 }}>
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    Configurez vos horaires d'ouverture pour chaque jour de la semaine. 
                    Les clients pourront réserver uniquement pendant ces créneaux.
                  </Typography>
                </Alert>

                <Grid container spacing={1.5}>
                  {daysOfWeek.map(({ key, label, short }) => (
                    <Grid item xs={12} sm={6} lg={4} key={key}>
                      <Card 
                        variant="outlined"
                        sx={{
                          bgcolor: schedule[key]?.closed ? 'grey.50' : 'background.paper',
                          border: schedule[key]?.closed ? '1px solid' : '2px solid',
                          borderColor: schedule[key]?.closed ? 'grey.300' : 'primary.main',
                          transition: 'all 0.2s'
                        }}
                      >
                        <CardContent sx={{ p: 1.5 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {isMobile ? short : label}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={!schedule[key]?.closed}
                                  onChange={(e) => handleScheduleChange(key, 'closed', !e.target.checked)}
                                  color="primary"
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2" color={schedule[key]?.closed ? 'text.disabled' : 'primary.main'}>
                                  {schedule[key]?.closed ? 'Fermé' : 'Ouvert'}
                                </Typography>
                              }
                            />
                          </Box>

                          <Collapse in={!schedule[key]?.closed}>
                            <Box display="flex" flexDirection={isSmallMobile ? 'column' : 'row'} gap={1}>
                              <TimePicker
                                label="Ouverture"
                                value={schedule[key]?.open ? dayjs(schedule[key].open, 'HH:mm') : null}
                                onChange={(time) => handleTimeChange(key, 'open', time)}
                                slotProps={{ 
                                  textField: { 
                                    size: 'small',
                                    fullWidth: isSmallMobile
                                  } 
                                }}
                              />
                              <TimePicker
                                label="Fermeture"
                                value={schedule[key]?.close ? dayjs(schedule[key].close, 'HH:mm') : null}
                                onChange={(time) => handleTimeChange(key, 'close', time)}
                                slotProps={{ 
                                  textField: { 
                                    size: 'small',
                                    fullWidth: isSmallMobile
                                  } 
                                }}
                              />
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Box mt={2} display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    onClick={saveSchedule}
                    disabled={loading}
                    sx={{ 
                      minWidth: 180,
                      py: 1,
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }}
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder les horaires'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Scroll Arrow */}
        {showScrollArrow && (
          <Fab
            color="primary"
            size="small"
            onClick={scrollToBottom}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <ArrowDownIcon />
          </Fab>
        )}

        {/* Service Dialog */}
        <Dialog 
          open={serviceDialogOpen} 
          onClose={() => setServiceDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {editingService ? 'Modifier le service' : 'Ajouter un service'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Définissez les détails de votre service
                </Typography>
              </Box>
              {isSmallMobile && (
                <IconButton 
                  onClick={() => setServiceDialogOpen(false)}
                  sx={{ ml: 'auto' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom du service"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Coupe femme, Coloration, Manucure..."
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Durée (minutes)"
                  type="number"
                  value={serviceForm.duration}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  inputProps={{ min: 15, max: 480, step: 15 }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix (€)"
                  type="number"
                  value={serviceForm.price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                  inputProps={{ min: 0, step: 0.5 }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button 
              onClick={() => setServiceDialogOpen(false)}
              variant="outlined"
              fullWidth={isSmallMobile}
              size="small"
            >
              Annuler
            </Button>
            <Button 
              onClick={saveService} 
              variant="contained"
              fullWidth={isSmallMobile}
              sx={{ ml: isSmallMobile ? 0 : 1, mt: isSmallMobile ? 1 : 0 }}
              size="small"
            >
              {editingService ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ScheduleTab;
